/**
 * Shared constants and pure helpers for the useChatApp modules.
 * Split out of useChatApp.js unchanged (2026-09-25 gap closure).
 */
import { reverseGeocode } from '../../api/geocodeApi';

/** Near-me / vicinity intents that require browser geolocation. */
export const NEAR_ME_RE = /\b(near me|around me|around here|nearby|my (location|area|vicinity)|where i am|current location|in my (area|vicinity))\b/i;

/** User refers to a zip already established in this chat thread. */
export const MY_ZIP_RE = /\bmy zip(?:\s*code)?\b/i;

/** US ZIP (optionally ZIP+4). */
const ZIP_CODE_RE = /\b(\d{5})(?:-\d{4})?\b/;

/** Agent error codes meaning the turn was refused before anything ran. */
export const AUTH_ERROR_CODES = new Set(['auth_required', 'account_mismatch', 'auth_unavailable']);

/**
 * Turn liveness bounds (2026-09-25 adversarial pass 1). The old agent
 * (langchain-agent main) sends `error` with no `done` after a graph failure,
 * and rollout runs this client against it, so a turn cannot wait on `done`
 * forever.
 *
 * TURN_WATCHDOG_MS: longest a sent turn may go without `done`. The agent caps
 * the graph at TURN_TIMEOUT_SECONDS=240 (langchain-agent
 * src/config/settings.py:64) after a quota call bounded by
 * HTTP_TIMEOUT_DEFAULT=30 (settings.py:76), so 300s sits above the worst
 * normal turn with margin.
 *
 * ERROR_DONE_GRACE_MS: the new agent writes `done` right after every `error`
 * (at most a `confirmation_request` in between; websocket.py _send_error and
 * the end-of-turn block). An `error` followed by nothing for this long is the
 * old agent's done-less error.
 */
export const TURN_WATCHDOG_MS = 300000;
export const ERROR_DONE_GRACE_MS = 4000;
/**
 * Per-principal socket cap (2026-09-25 adversarial pass 14). The agent refuses
 * a socket over its cap with error{code:'too_many_connections'} + done + close
 * 1013 (langchain-agent src/server/socket_cap.py). A socket retired mid-turn
 * keeps its server slot until that turn ends, so the refused socket is
 * replaced ONCE after AGENT_CAP_RETRY_MS; a further refusal needs a further
 * user send (an open socket holds no slot until it sends).
 */
export const TOO_MANY_CONNECTIONS_CLOSE_CODE = 1013;
// 2026-09-25 adversarial pass 15: a proxy in front of the agent may not pass
// 1013 through, so the error frame sent before the close marks the refusal.
export const TOO_MANY_CONNECTIONS_CODE = 'too_many_connections';
export const AGENT_CAP_RETRY_MS = 5000;
/**
 * Reconnect after an unexpected close (2026-09-25 adversarial pass 19): the
 * agent may close 1011 after an internal error, and production has no Retry
 * control (dev-only header), so the chat stayed offline until reload. The
 * delay doubles per drop since the last `done`, capped, so a dead or
 * flapping agent is retried at most every AGENT_DROP_RECONNECT_MAX_MS.
 */
export const AGENT_DROP_RECONNECT_BASE_MS = 1000;
export const AGENT_DROP_RECONNECT_MAX_MS = 30000;
/** @param {number} drops - Drops since the last completed turn. */
export function agentDropReconnectDelayMs(drops) {
  const n = Math.min(Math.max(0, Math.floor(drops) || 0), 16);
  return Math.min(AGENT_DROP_RECONNECT_BASE_MS * 2 ** n, AGENT_DROP_RECONNECT_MAX_MS);
}
export const TOO_MANY_CONNECTIONS_MESSAGE =
  'Too many open agent tabs for this account. Close another chat tab or window, then send again.';

export const TURN_WATCHDOG_MESSAGE =
  'The assistant did not finish responding, so the connection was reset. Please try again.';

/**
 * Chat `history` bounds (2026-09-25 adversarial pass 1). The agent refuses a
 * frame over 256,000 chars and keeps only the last 40 items, 8,000 chars each
 * (langchain-agent websocket.py / session_store.py). Uncapped, one long reply
 * pushed every later turn in the thread over the frame limit.
 */
export const HISTORY_MAX_ITEMS = 40;
export const HISTORY_ITEM_MAX_CHARS = 8000;
// Serialized budget for all history items, well under the frame cap so the
// message itself (and JSON escaping) still fits.
export const HISTORY_MAX_JSON_CHARS = 160000;
/**
 * The agent refuses a whole frame longer than this ("Message too large.",
 * langchain-agent websocket.py). Its len() counts code points; a JS string
 * length counts UTF-16 units, which is never fewer, so this bound is safe.
 */
export const AGENT_FRAME_MAX_CHARS = 256000;
// Headroom for the "history" key itself and rounding.
const FRAME_MARGIN_CHARS = 1000;
/**
 * 2026-09-25 adversarial pass 15: the agent also caps a frame at 1 MiB of
 * UTF-8 (uvicorn --ws-max-size 1048576, langchain-agent/Dockerfile) and closes
 * an over-size frame with 1009, killing the socket instead of refusing the
 * turn. History always fits (boundedHistory), so only the typed message can
 * overflow; it is refused here before anything is sent. A frame within
 * AGENT_FRAME_MAX_CHARS UTF-16 units is at most 3 bytes per unit (768,000
 * bytes), so this one char bound also keeps every frame under 1 MiB.
 * OUTBOUND_OVERHEAD_CHARS covers the location/zip suffix and metadata.
 */
const OUTBOUND_OVERHEAD_CHARS = 4000;
export const MESSAGE_TOO_LONG_MESSAGE =
  'This message is too long to send. Shorten it (or split it) and try again.';

// Guardian chat message cap (audit P-7, 2026-10-21). Must match the agent's
// GUARDIAN_MAX_MESSAGE_CHARS (default 8,000); the agent enforces it anyway
// (error code message_too_long), this only spares the user a round trip.
export const GUARDIAN_MAX_MESSAGE_CHARS = 8000;

/**
 * True when `text` cannot fit in one agent frame, whatever history is sent.
 * @param {string} text
 * @param {string|null|undefined} token
 */
export function messageTooLongForFrame(text, token) {
  const core = JSON.stringify({ message: text, token: token ?? null }).length;
  return core + OUTBOUND_OVERHEAD_CHARS > AGENT_FRAME_MAX_CHARS;
}

/**
 * Newest-first bounded history for the chat payload: last HISTORY_MAX_ITEMS
 * turns, each trimmed to the server's hydrate cap, oldest dropped once the
 * serialized budget is spent. Order is preserved (oldest first).
 * @param {Array<{type: string, content: string}>} priorMessages
 * @param {number} [reservedChars] - Serialized size of the rest of the frame.
 * @returns {Array<{role: string, content: string}>}
 */
export function boundedHistory(priorMessages, reservedChars = 0) {
  const out = [];
  let used = 0;
  // 2026-09-25 adversarial pass 5: the budget is what the rest of the frame
  // leaves, not a fixed 160k -- a long message plus full history exceeded the
  // agent's frame cap and the whole turn was refused.
  const budget = Math.min(
    HISTORY_MAX_JSON_CHARS,
    AGENT_FRAME_MAX_CHARS - FRAME_MARGIN_CHARS - Math.max(0, reservedChars),
  );
  const recent = priorMessages.slice(-HISTORY_MAX_ITEMS);
  for (let i = recent.length - 1; i >= 0; i -= 1) {
    const m = recent[i];
    // 2026-09-25 adversarial pass 2: the cut is in UTF-16 units; one between
    // a surrogate pair left a lone high surrogate, which the agent's LLM call
    // cannot UTF-8 encode (every turn of the hydrated session then failed).
    // const content = String(m.content).trim().slice(0, HISTORY_ITEM_MAX_CHARS);
    let content = String(m.content).trim().slice(0, HISTORY_ITEM_MAX_CHARS);
    const last = content.charCodeAt(content.length - 1);
    if (last >= 0xd800 && last <= 0xdbff) content = content.slice(0, -1);
    const item = { role: m.type === 'user' ? 'user' : 'assistant', content };
    const size = JSON.stringify(item).length + 1;
    // if (used + size > HISTORY_MAX_JSON_CHARS) break;  // pre-pass-5 fixed budget
    if (used + size > budget) break;
    used += size;
    out.push(item);
  }
  return out.reverse();
}

/** Search radius for near-me fire / env checks (50 km). */
export const NEAR_ME_BUFFER_METERS = 50000;

/** Matches the agent's VOICE_AUTH_REQUIRED 413 limit — reject client-side before uploading. */
export const MAX_VOICE_BLOB_BYTES = 10 * 1024 * 1024;

export const LOCATION_DENIED_MSG =
  'Because you denied location access, Guardian cannot see if there are any fires '
  + 'in your vicinity. Enable location for this site in your browser settings and ask again, '
  + 'or name a place (for example, “fires near Malibu”).';
// Longest a near-me turn waits on the browser location prompt.
export const LOCATION_WATCHDOG_MS = 30000;
// Longest the place-name lookup may take once the browser has answered; the
// turn then proceeds with coordinates only (2026-09-25 pass 7). Enforced here
// as well as in geocodeApi so it holds even without AbortSignal support.
const GEOCODE_GRACE_MS = 6000;

export function boundedReverseGeocode(token, lat, lon) {
  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve(null), GEOCODE_GRACE_MS);
  });
  return Promise.race([reverseGeocode(token, lat, lon), timeout]).finally(() =>
    clearTimeout(timer),
  );
}
export const LOCATION_TIMEOUT_MSG =
  'Location was not shared in time. Try Share location from the + menu, or name a place.';
export const LOCATION_LATE_MSG =
  'Got your location. Please ask your question again.';
// Voice notices (2026-09-25 gap closure).
export const SPOKEN_REPLY_SKIPPED_MSG = 'Spoken reply skipped while you were recording.';
export const VOICE_OFFLINE_DRAFT_MSG = 'Not connected — your words are in the box; send when reconnected.';
export const VOICE_AUTH_DRAFT_MSG = 'Sign in to send — your words are in the box.';

export function extractZipCode(text) {
  if (!text || typeof text !== 'string') return null;
  const match = text.match(ZIP_CODE_RE);
  return match ? match[1] : null;
}

/** Recover last zip from prior user messages when sessionPlace was never saved. */
export function placeFromMessages(messages) {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m?.type !== 'user') continue;
    const zip = extractZipCode(m.content);
    if (zip) return { type: 'zip', value: zip, label: zip };
  }
  return null;
}

/** Returns true when the string is a valid ws:// or wss:// URL. */
export function isValidWsUrl(maybeUrl) {
  try {
    const parsed = new URL(maybeUrl);
    return parsed.protocol === 'wss:' || parsed.protocol === 'ws:';
  } catch {
    return false;
  }
}

/** Upgrades ws:// to wss:// when the page is served over HTTPS (mixed-content safe). */
export function normalizeWsUrlForEnv(url) {
  try {
    const parsed = new URL(url);
    if (window.location.protocol === 'https:' && parsed.protocol === 'ws:') {
      parsed.protocol = 'wss:';
      return parsed.toString();
    }
    return url;
  } catch {
    return url;
  }
}
